import { CalendarCheck } from "lucide-react";

import { PropPeek } from "@/components/ui/PropPeek";
import { useTasks } from "@/hooks/useTasks";

import type { TaskUiState } from "../../../domain/task-state";

interface TasksPeekProps {
  /** Deep-link to the full /tasks page. */
  onOpenTasks: () => void;
}

// Status labels/dots mirror the Task Manager's sections (task-state.ts
// dispositions), NOT raw praxis states — the peek shows exactly what the
// full /tasks board shows.
const STATE_LABEL: Record<TaskUiState, string> = {
  held: "Waiting on you",
  working: "Working",
  todo: "To do",
  done: "Done",
};

const STATE_DOT: Record<TaskUiState, string> = {
  held: "bg-home-yellow",
  working: "bg-brand-coral",
  todo: "bg-black/30 dark:bg-white/30",
  done: "bg-accent-green",
};

/**
 * The calendar "door": a glanceable list of what's on the agent's plate,
 * opening the full /tasks board. Mirrors InboxPeek / IntegrationsPeek /
 * NotebookQuickView — the world points at the page, it doesn't re-host it.
 * Subscribes to the same server-bucketed tasks surface as `/tasks`
 * (`useTasks`), so execution/validation beads and dormant/archived states
 * never appear here; held ("waiting on you") items lead the list.
 */
export function TasksPeek({ onOpenTasks }: TasksPeekProps) {
  const { current, held, isLoading } = useTasks();

  const open = [...held, ...current];
  const shown = open.slice(0, 6);

  return (
    <PropPeek
      icon={CalendarCheck}
      title="Task Manager"
      meta={isLoading ? undefined : open.length > 0 ? `${open.length} open` : "All clear"}
      openLabel="Open task manager"
      onOpenFull={onOpenTasks}
    >
      {isLoading ? (
        <p className="px-2 py-6 text-center text-[13px] text-black/40 dark:text-white/40">Loading tasks…</p>
      ) : shown.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
          <CalendarCheck className="h-7 w-7 text-black/25 dark:text-white/25" strokeWidth={1.8} />
          <p className="text-[13px] text-black/45 dark:text-white/45">
            Nothing on your plate yet. Clawbert adds tasks here as you work together.
          </p>
        </div>
      ) : (
        <ul className="space-y-0.5 px-1">
          {shown.map((item) => (
            <li key={item.id} className="flex items-center gap-2.5 rounded-lg px-2 py-2">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATE_DOT[item.displayState]}`} />
              <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-black dark:text-white">
                {item.title}
              </span>
              <span className="ml-auto shrink-0 text-[11px] font-semibold text-black/40 dark:text-white/40">
                {STATE_LABEL[item.displayState]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </PropPeek>
  );
}
